import { useForm, usePage } from '@inertiajs/react';
import { useState } from 'react';

import EmailIcon from '@mui/icons-material/Email';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

function EmailVerificationDialog() {
  const { auth } = usePage().props;
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Only show dialog if user is logged in and email is not verified
  const showDialog = auth?.user && !auth.user.email_verified_at;

  const form = useForm();

  const handleResendVerification = () => {
    setIsSending(true);
    setSendSuccess(false);

    form.post(route('verification.send'), {
      onSuccess: () => {
        setSendSuccess(true);
        setIsSending(false);
      },
      onError: () => {
        setIsSending(false);
      },
    });
  };

  if (!showDialog) {
    return null;
  }

  return (
    <Dialog
      open={showDialog}
      aria-labelledby="email-verification-dialog-title"
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      disableAutoFocus
      disableEnforceFocus
      onClose={() => {
        // Prevent dialog from closing by any means
        return false;
      }}
    >
      <DialogTitle id="email-verification-dialog-title" sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center">
          <EmailIcon color="primary" sx={{ mr: 1.5 }} />
          <Typography variant="h6">Verify Your Email Address</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          To access all features and ensure the security of your account, please
          verify your email address. We've sent a verification link to{' '}
          <strong>{auth.user.email}</strong>.
        </DialogContentText>

        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="body2" gutterBottom>
            If you haven't received the email:
          </Typography>
          <Typography component="ul" variant="body2" sx={{ pl: 2 }}>
            <li>Check your spam or junk folder</li>
            <li>Make sure you entered the correct email address</li>
            <li>Click the "Resend Verification Email" button below</li>
          </Typography>
        </Box>

        {sendSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Verification email has been sent! Please check your inbox.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleResendVerification}
          variant="contained"
          color="primary"
          disabled={isSending}
          fullWidth
          startIcon={
            isSending ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {isSending ? 'Sending...' : 'Resend Verification Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EmailVerificationDialog;
